// Serviço de API para SIAPESUS
// Centraliza todas as chamadas para a API usando as configurações

import API_CONFIG, { 
  buildApiUrl, 
  getAuthHeaders, 
  buildUrlWithParams, 
  fetchWithCache, 
  validateApiResponse, 
  handleApiError,
  getPaginationConfig 
} from '../config/api';

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.token = null;
  }

  // Definir token de autenticação
  setToken(token) {
    this.token = token;
  }

  // Obter token atual
  getToken() {
    return this.token || localStorage.getItem('authToken');
  }

  // Fazer requisição autenticada
  async authenticatedRequest(endpoint, options = {}) {
    const token = this.getToken();
    
    if (!token) {
      this.logout();
      window.location.href = '/adm_i9/login';
      throw new Error('Token de autenticação não encontrado');
    }

    const url = buildApiUrl(endpoint);
    const headers = getAuthHeaders(token);

    try {
      return await this.request(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });
    } catch (error) {
      // Se o erro for 401 (não autorizado), limpar token e redirecionar
      if (error.message.includes('401') || error.message.includes('401') || 
          error.message.includes('Token expirado') || error.message.includes('Token inválido') ||
          error.message.includes('Sessão expirada') || error.message.includes('não autenticado')) {
        this.logout();
        // Redirecionar para login se não estiver já na página de login
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/adm_i9/login';
        }
        throw new Error('Sessão expirada. Redirecionando para login...');
      }
      throw error;
    }
  }

  // Fazer requisição simples
  async request(url, options = {}) {
    try {
      const response = await fetchWithCache(url, {
        ...options,
        headers: {
          ...API_CONFIG.DEFAULT_HEADERS,
          ...options.headers
        }
      });

      // Verificar se a resposta é 401 ou 403 antes de processar JSON
      if (response.status === 401 || response.status === 403) {
        // Tentar obter a mensagem de erro da resposta
        let errorData = null;
        try {
          const responseClone = response.clone();
          errorData = await responseClone.json();
        } catch (e) {
          // Se não conseguir fazer parse, continuar mesmo assim
        }
        
        // Se a mensagem indicar erro de token, fazer logout e redirecionar
        if (!errorData || !errorData.error || 
            String(errorData.error).toLowerCase().includes('token') ||
            String(errorData.error).toLowerCase().includes('expirado') ||
            String(errorData.error).toLowerCase().includes('inválido') ||
            String(errorData.error).toLowerCase().includes('invalid') ||
            String(errorData.error).toLowerCase().includes('não autenticado') ||
            String(errorData.error).toLowerCase().includes('unauthorized')) {
          this.logout();
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/adm_i9/login';
          }
          throw new Error('Sessão expirada. Redirecionando para login...');
        }
      }

      // Tentar obter JSON da resposta
      let data;
      try {
        // Se response.json já foi chamado (do cache), usar diretamente
        if (response._jsonData) {
          data = response._jsonData;
        } else {
          data = await response.json();
        }
      } catch (jsonError) {
        // Se não conseguir fazer parse do JSON, pode ser erro de rede
        console.error('Erro ao fazer parse do JSON:', jsonError);
        throw jsonError;
      }
      
      // Verificar ANTES de validar se há erro de token na resposta
      if (data && typeof data === 'object' && !data.success && data.error) {
        const errorMessage = String(data.error).toLowerCase();
        if (errorMessage.includes('token') || 
            errorMessage.includes('expirado') || 
            errorMessage.includes('inválido') ||
            errorMessage.includes('invalid') ||
            errorMessage.includes('não autenticado') ||
            errorMessage.includes('unauthorized') ||
            errorMessage.includes('401') ||
            errorMessage.includes('403') ||
            errorMessage.includes('sessão expirada') ||
            errorMessage.includes('forbidden')) {
          this.logout();
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/adm_i9/login';
          }
          throw new Error('Sessão expirada. Redirecionando para login...');
        }
      }
      
      // Se chegou aqui, validar a resposta normalmente
      const validatedResponse = validateApiResponse(data);
      return validatedResponse;
    } catch (error) {
      // Se já foi tratado como 401/token, propagar o erro
      if (error.message.includes('401') || 
          error.message.includes('Sessão expirada') || 
          error.message.includes('Redirecionando')) {
        throw error;
      }
      throw new Error(handleApiError(error));
    }
  }

  // ===== AUTENTICAÇÃO =====
  
  async login(username, password) {
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.AUTH.LOGIN);
    
    const response = await this.request(url, {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });

    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response;
  }

  async verifyToken() {
    const token = this.getToken();
    if (!token) {
      throw new Error('Token não encontrado');
    }

    const url = buildApiUrl(API_CONFIG.ENDPOINTS.AUTH.VERIFY);
    return this.authenticatedRequest(url, {
      method: 'GET'
    });
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  }

  // ===== CONSULTAS GERAIS =====

  async getUnidades() {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.QUERIES.UNIDADES);
  }

  async getProfissionais() {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.QUERIES.PROFISSIONAIS);
  }

  async getProcedimentos() {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.QUERIES.PROCEDIMENTOS);
  }

  async getCategorias() {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.QUERIES.CATEGORIAS);
  }

  // ===== PRODUÇÃO BPA =====

  async getDadosProducao(filtros = {}, pagination = {}) {
    const { page, limit } = getPaginationConfig(pagination.page, pagination.limit);
    
    const payload = {
      ...filtros,
      page,
      limit
    };

    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.BPA.DADOS_PRODUCAO, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async gerarBPA(filtros = {}) {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.BPA.GERAR, {
      method: 'POST',
      body: JSON.stringify(filtros)
    });
  }

  async listTransportBpaBoardings(filtros = {}) {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.BPA.TRANSPORTE_EMBARQUES, {
      method: 'POST',
      body: JSON.stringify(filtros)
    });
  }

  async printTransportBpa(filtros = {}) {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.BPA.TRANSPORTE_RELATORIO, {
      method: 'POST',
      body: JSON.stringify(filtros)
    });
  }

  async generateTransportBpa(filtros = {}) {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.BPA.TRANSPORTE_GERAR, {
      method: 'POST',
      body: JSON.stringify(filtros)
    });
  }

  async buscarBPAGerados(filtros = {}, pagination = {}) {
    const { page, limit } = getPaginationConfig(pagination.page, pagination.limit);
    
    const params = {
      ...filtros,
      page,
      limit
    };

    const url = buildUrlWithParams(API_CONFIG.ENDPOINTS.BPA.BUSCAR_GERADOS, params);
    return this.authenticatedRequest(url, {
      method: 'GET'
    });
  }

  // ===== CABEÇALHOS BPA =====

  async buscarCabecalhos(filtros = {}, pagination = {}) {
    const { page, limit } = getPaginationConfig(pagination.page, pagination.limit);
    
    const payload = {
      ...filtros,
      page,
      limit
    };

    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.BPA_CABECALHO.BUSCAR, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async deletarCabecalho(id) {
    const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.BPA_CABECALHO.DELETAR}/${id}`);
    return this.authenticatedRequest(url, {
      method: 'DELETE'
    });
  }

  async gerarBPAFromCabecalho(id) {
    const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.BPA_CABECALHO.GERAR_BPA}/${id}/gerar-bpa`);
    return this.authenticatedRequest(url, {
      method: 'POST'
    });
  }

  // ===== TRANSPORT =====

  async listTransportResources() {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.TRANSPORT.RESOURCES, {
      method: 'GET'
    });
  }

  async listTransport(resource, params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });

    const endpoint = `${API_CONFIG.ENDPOINTS.TRANSPORT.BASE}/${resource}${query.toString() ? `?${query.toString()}` : ''}`;
    return this.authenticatedRequest(endpoint, {
      method: 'GET'
    });
  }

  async getTransport(resource, id) {
    return this.authenticatedRequest(`${API_CONFIG.ENDPOINTS.TRANSPORT.BASE}/${resource}/${id}`, {
      method: 'GET'
    });
  }

  async createTransport(resource, payload) {
    return this.authenticatedRequest(`${API_CONFIG.ENDPOINTS.TRANSPORT.BASE}/${resource}`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async updateTransport(resource, id, payload) {
    return this.authenticatedRequest(`${API_CONFIG.ENDPOINTS.TRANSPORT.BASE}/${resource}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  async deleteTransport(resource, id) {
    return this.authenticatedRequest(`${API_CONFIG.ENDPOINTS.TRANSPORT.BASE}/${resource}/${id}`, {
      method: 'DELETE'
    });
  }

  async listTransportReports(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.TRANSPORT.REPORTS}${query.toString() ? `?${query.toString()}` : ''}`;
    return this.authenticatedRequest(endpoint, { method: 'GET' });
  }

  async printTransportReport(payload = {}) {
    return this.authenticatedRequest(API_CONFIG.ENDPOINTS.TRANSPORT.REPORTS_PDF, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async printRideRoteiro(rideId) {
    return this.authenticatedRequest(`${API_CONFIG.ENDPOINTS.TRANSPORT.BASE}/rides/${rideId}/roteiro/pdf`, {
      method: 'POST',
    });
  }

  async lookupEsusCidadaoByCpf(cpf, mode = 'patient') {
    const query = new URLSearchParams({
      cpf: String(cpf || '').replace(/\D/g, ''),
      mode: mode === 'companion' ? 'companion' : 'patient',
    });
    return this.authenticatedRequest(`${API_CONFIG.ENDPOINTS.TRANSPORT.ESUS_CIDADAO}?${query.toString()}`, {
      method: 'GET'
    });
  }

  async lookupPatientsByBirthDate(birthDate) {
    const query = new URLSearchParams({
      birth_date: String(birthDate || ''),
    });
    return this.authenticatedRequest(`${API_CONFIG.ENDPOINTS.TRANSPORT.ESUS_CIDADAO}?${query.toString()}`, {
      method: 'GET'
    });
  }

  // ===== UTILITÁRIOS =====

  async healthCheck() {
    const url = buildApiUrl(API_CONFIG.ENDPOINTS.HEALTH);
    return this.request(url, {
      method: 'GET'
    });
  }

  // Download de arquivo
  async downloadFile(url, filename) {
    const token = this.getToken();
    const headers = getAuthHeaders(token);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Erro no download: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { success: true, message: 'Download iniciado' };
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Upload de arquivo
  async uploadFile(endpoint, file, additionalData = {}) {
    const token = this.getToken();
    const formData = new FormData();
    
    formData.append('file', file);
    
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const url = buildApiUrl(endpoint);
    
    return this.request(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
        // Não definir Content-Type para FormData
      },
      body: formData
    });
  }

  // ===== CACHE E PERFORMANCE =====

  // Limpar cache
  clearCache() {
    // Implementar limpeza de cache se necessário
  }

  // Verificar conectividade
  async checkConnectivity() {
    try {
      await this.healthCheck();
      return { connected: true, message: 'Conectado' };
    } catch (error) {
      return { connected: false, message: error.message };
    }
  }

  // ===== CONFIGURAÇÕES =====

  // Obter configurações da API
  getConfig() {
    return {
      baseURL: this.baseURL,
      endpoints: API_CONFIG.ENDPOINTS,
      timeout: API_CONFIG.TIMEOUT,
      pagination: API_CONFIG.PAGINATION
    };
  }

  // Atualizar configurações
  updateConfig(newConfig) {
    Object.assign(API_CONFIG, newConfig);
    this.baseURL = API_CONFIG.BASE_URL;
  }
}

// Instância singleton do serviço
const apiService = new ApiService();

export default apiService;
